import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { productService } from '../services/productService';

/**
 * Catálogo parcial de productos: búsqueda bajo demanda + caché por ID.
 */
export function useProductCatalog(warehouseId = null) {
  const [byId, setById] = useState({});
  const byIdRef = useRef(byId);

  useEffect(() => {
    byIdRef.current = byId;
  }, [byId]);

  const mergeProducts = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    setById((prev) => {
      const next = { ...prev };
      list.forEach((product) => {
        if (product?.id != null) {
          next[product.id] = { ...prev[product.id], ...product };
        }
      });
      return next;
    });
  }, []);

  const getProduct = useCallback(
    (id, fallback = null) => {
      if (id == null || id === '') return fallback;
      const numericId = parseInt(id, 10);
      if (!numericId) return fallback;
      return byId[numericId] || fallback;
    },
    [byId],
  );

  const ensureByIds = useCallback(
    async (ids) => {
      const unique = [...new Set((ids || []).map((id) => parseInt(id, 10)).filter(Boolean))];
      const currentById = byIdRef.current;
      const missing = unique.filter((id) => !currentById[id]);
      if (missing.length === 0) return [];

      const response = await productService.batchForForm(missing, warehouseId);
      const list = response?.data || [];
      mergeProducts(list);
      return list;
    },
    [mergeProducts, warehouseId],
  );

  const searchProducts = useCallback(
    async (query) => {
      const response = await productService.searchForForm(query, warehouseId);
      const list = response?.data || [];
      mergeProducts(list);
      return list;
    },
    [mergeProducts, warehouseId],
  );

  const resolveIdentifiers = useCallback(
    async (identifiers) => {
      const response = await productService.resolveIdentifiersForForm(identifiers, warehouseId);
      const list = response?.data || [];
      mergeProducts(list);
      return list;
    },
    [mergeProducts, warehouseId],
  );

  const products = useMemo(() => Object.values(byId), [byId]);

  return {
    products,
    byId,
    getProduct,
    mergeProducts,
    ensureByIds,
    searchProducts,
    resolveIdentifiers,
  };
}
